<template>
  <component
    :is="element"
    :to="path"
    :class="[
      'p-2 md:p-3 block',
      { 'transition duration-200 ease-in-out hover:bg-blue-100 cursor-pointer dark:hover:bg-blue-900/50' : path },
      { 'opacity-75 bg-green-100 dark:bg-green-800/20': isResource }
    ]"
    tag="div"
  >
    <div v-if="employee && employee.displayName" class="flex items-center">
      <o-avatar v-if="!isResource && !isSectionSizeSmall" :items="[employee]" rounded="rounded-xl" :size="false" base-class="first:ml-0 border-white flex items-center justify-center w-8 h-8 -ml-3 text-xs mr-2" />
      <div>
        <p class="font-medium text-sm leading-none">
          {{ employee.displayName }}
        </p>
        <p v-if="roster" class="text-xs opacity-80 leading-none mt-1">
          <template v-if="hour">
            <template v-if="!isEmployeeHourRosterWeekA">
              <o-chip size="2xs" variant="success" class="mr-1">
                {{ rosterWeekLabel }}
              </o-chip>
            </template>
            <span class="mt-1">{{ hour.startTimeLabel }} - {{ hour.finishTimeLabel }}</span>
          </template>
          <template v-else>
            -
          </template>
        </p>

        <p v-if="hour && hasEmployeeHourLabel" class="text-2xs text-gray-600 leading-none dark:text-gray-300">
          ({{ hour.label }})
        </p>
      </div>
    </div>
    <slot />
  </component>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'

import { EmployeeType } from '@/_api/models/employee'
import { EmployeeHourRosterWeek, EmployeeHourRosterWeekLabels } from '@/_api/models/employeeHour'

import {
  OAvatar,
} from '@/components/elements'

import { useUIStore } from '~/stores/useUIStore'

const uiStore = useUIStore()
const { t } = useI18n()

interface Props {
  section: any
}

const props = defineProps<Props>()

const employee = computed(() => props.section.employee)
const roster = computed(() => props.section.roster)
const date = computed(() => props.section.date)
const hour = computed(() => (roster.value?.hour?.working) ? roster.value.hour : null)
const sectionSize = computed(() => uiStore.schedule.sectionSize)
const isSectionSizeSmall = computed(() => sectionSize.value < 200)
const path = computed(() => date.value && employee.value ? `/schedule/week?date=${date.value.date}&employees=${employee.value.idEmployee}` : null)
// Use the imported component reference (not the string `'nuxt-link'`) —
// Nuxt 3/4 resolves <NuxtLink> via auto-import at compile time, so string
// lookups against a dynamic `:is=` don't resolve the component and the link
// ends up rendered as an unknown element with no navigation handler.
// Matches the pattern in app/components/layout/MenuItem.vue.
const element = computed(() => path.value ? NuxtLink : 'div')
const isResource = computed(() => employee.value?.type === EmployeeType.Resource)
const isEmployeeHourRosterWeekA = computed(() => !hour.value || (hour.value && hour.value.rosterWeek === EmployeeHourRosterWeek.a))
const hasEmployeeHourLabel = computed(() => typeof hour.value?.label === 'string' && hour.value.label.length > 0)
const rosterWeekLabel = computed(() => {
  // rosterWeek is a numeric TypeScript enum (EmployeeHourRosterWeek.a = 1, etc.)
  // — the label lives in the sibling Labels map, not on the value itself.
  // Previously this read `.description` which only worked in the Nuxt 2 days
  // when rosterWeek was an object.
  const week = hour.value?.rosterWeek
  if (week == null) return ''
  const key = EmployeeHourRosterWeekLabels[week as EmployeeHourRosterWeek]
  if (!key) return ''
  try {
    return t(key)
  } catch {
    return key
  }
})
</script>
